import { useCallback, useRef, useState } from "react";
import type {
    AiqmUploadWSMessage,
    AiqmUploadWSProgressMessage,
} from "@type/aiqm";
import { getCookie } from "@util/cookie";

interface UseAiqmUploadProgressReturn {
    connected: boolean;
    latestMessage: AiqmUploadWSProgressMessage | null;
    connect: () => void;
    disconnect: () => void;
}

/**
 * Real-time per-file progress for AIQM bulk audio upload, mirroring the bulk-CV
 * upload WebSocket (`use-bulk-upload-progress.ts`). Connects to
 * `ws://<host>/training/aiqm/ws/{userId}` and surfaces `progress` events
 * (one per file: `completed` / `failed`, with `batch_id` + `current`/`total`).
 *
 * The caller filters by its own `batch_id` so events from another batch don't
 * touch this batch's UI, and calls `disconnect()` when the batch finishes
 * (`current === total`) or the screen unmounts.
 */
export const useAiqmUploadProgress = (
    userId: string,
    apiBaseUrl: string,
    onProgress?: (msg: AiqmUploadWSProgressMessage) => void,
    // The batch to resume. When present (e.g. after a page reload), connect with
    // `?batch_id=<id>` so the server REPLAYS the latest per-file snapshot frames
    // for that batch (updated BE contract #3575/#3576/#3579). Read via a ref so
    // supplying it doesn't churn connect()'s identity mid-upload.
    batchId?: string | null,
): UseAiqmUploadProgressReturn => {
    const [connected, setConnected] = useState(false);
    const [latestMessage, setLatestMessage] =
        useState<AiqmUploadWSProgressMessage | null>(null);
    const socketRef = useRef<WebSocket | null>(null);
    const reconnectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const pingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const shouldReconnectRef = useRef(false);
    // Keep the latest callback without forcing connect() to re-create on each render.
    const onProgressRef = useRef(onProgress);
    onProgressRef.current = onProgress;
    const batchIdRef = useRef(batchId);
    batchIdRef.current = batchId;

    const cleanup = useCallback(() => {
        if (reconnectTimeoutRef.current) {
            clearTimeout(reconnectTimeoutRef.current);
            reconnectTimeoutRef.current = null;
        }
        if (pingIntervalRef.current) {
            clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = null;
        }
    }, []);

    const disconnect = useCallback(() => {
        shouldReconnectRef.current = false;
        cleanup();
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }
        setConnected(false);
    }, [cleanup]);

    const connect = useCallback(() => {
        if (!userId) return;

        cleanup();
        if (socketRef.current) {
            socketRef.current.close();
            socketRef.current = null;
        }

        shouldReconnectRef.current = true;
        setLatestMessage(null);

        // On a resume (batchId set), ask the server to replay this batch's latest
        // per-file snapshot. Omitted on the initial connect (no batch yet) so the
        // URL is unchanged from before for a fresh upload.
        const bid = batchIdRef.current;
        const wsUrl =
            `${apiBaseUrl.replace("http", "ws")}/training/aiqm/ws/${userId}` +
            (bid ? `?batch_id=${encodeURIComponent(bid)}` : "");
        // After the BE security fix, the JWT is no longer read from the URL — it
        // must be sent as the WebSocket subprotocol: exactly ["bearer", <jwt>]
        // ("bearer" first, token second). Without it the server rejects with 403.
        // The URL carries no credential, so this second arg is REQUIRED.
        const token = getCookie("authToken");
        const ws = token
            ? new WebSocket(wsUrl, ["bearer", token])
            : new WebSocket(wsUrl);

        ws.onopen = () => {
            setConnected(true);
            if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
            pingIntervalRef.current = setInterval(() => {
                if (ws.readyState === WebSocket.OPEN) {
                    ws.send(JSON.stringify({ type: "ping" }));
                }
            }, 30000);
        };

        ws.onmessage = (event) => {
            try {
                const data: AiqmUploadWSMessage = JSON.parse(event.data);
                if ("type" in data && (data as { type?: string }).type === "pong") return;
                if (data.event === "connected") return;
                // Forward every PER-FILE result whatever the `event` label — the
                // BE sends a finished file as `event: "completed"` (also
                // "progress"/"error"/"cancelled"), each carrying a real
                // `session_id` + `status`. Key off `session_id` presence, NOT an
                // `event` allow-list: a batch-level "completed"/"cancelled" has no
                // session_id (safely ignored), while the per-file terminal event
                // that drives the toast is never dropped. The missing "completed"
                // case is exactly what left a finished upload stuck in "analyzing".
                if (data.session_id) {
                    setLatestMessage(data);
                    onProgressRef.current?.(data);
                }
            } catch (error) {
                console.error("Error parsing AIQM upload WS message:", error);
            }
        };

        ws.onerror = () => {
            setConnected(false);
        };

        ws.onclose = () => {
            setConnected(false);
            cleanup();
            // Reconnect only while a batch is still being tracked.
            if (shouldReconnectRef.current) {
                reconnectTimeoutRef.current = setTimeout(() => {
                    if (shouldReconnectRef.current) connect();
                }, 3000);
            }
        };

        socketRef.current = ws;
    }, [userId, apiBaseUrl, cleanup]);

    return { connected, latestMessage, connect, disconnect };
};
